import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TranscriptDisplay } from '@/entities/call'
import { ConversationQASection } from '@/entities/qa'
import { useChatConversation } from '@/entities/chatConversation'
import { messagesToTranscript } from '@/shared/lib/messagesToTranscript'
import { formatDateHistory } from '@/shared/lib/date'

interface ChatConversationDetailsProps {
  conversationId: string | null
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
}

export function ChatConversationDetails({ conversationId }: ChatConversationDetailsProps) {
  const { data: conversation, isLoading, error } = useChatConversation(conversationId)

  if (!conversationId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-muted-foreground">
        <p className="text-body">Select a conversation to see details</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  if (error || !conversation) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-red-500">
        <p className="text-body">Failed to load conversation</p>
      </div>
    )
  }

  const transcriptEvents = messagesToTranscript(conversation.messages)
  const autoQa = conversation.auto_qa && typeof conversation.auto_qa === 'object' && Object.keys(conversation.auto_qa).length > 0
    ? conversation.auto_qa
    : undefined

  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-body">
          Chat with {conversation.customer_id || 'Unknown'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 pb-4 space-y-4">
        {/* Metadata */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-detail">
          <div>
            <span className="text-muted-foreground">Customer ID</span>
            <p className="font-medium truncate">{conversation.customer_id || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Integration</span>
            <p className="font-medium">{conversation.integration_type}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Agent</span>
            <p className="font-medium">{conversation.agent_name || '-'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Status</span>
            <div>
              <Badge
                variant="outline"
                className={STATUS_COLORS[conversation.status] ?? 'bg-gray-100 text-gray-800'}
              >
                {conversation.status}
              </Badge>
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Created</span>
            <p className="font-medium">{formatDateHistory(new Date(conversation.created_at), 'MMM d, yyyy h:mm a')}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Updated</span>
            <p className="font-medium">{formatDateHistory(new Date(conversation.updated_at), 'MMM d, yyyy h:mm a')}</p>
          </div>
        </div>

        {/* Auto QA */}
        {autoQa && (
          <div className="space-y-3">
            <h3 className="text-detail font-medium text-muted-foreground">Automated QA</h3>
            <div className="p-3 bg-gray-50 rounded-md">
              <div className="space-y-2 text-body">
                {Object.entries(autoQa).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{key}: </span>
                    {typeof value === 'boolean' ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-detail font-medium ${
                        value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {value ? '✓ Yes' : '✗ No'}
                      </span>
                    ) : (
                      <span className="text-gray-900">{String(value)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Message thread */}
        <div>
          <h3 className="text-detail font-medium text-muted-foreground mb-2">Messages</h3>
          {transcriptEvents.length === 0 ? (
            <p className="text-detail text-muted-foreground text-center py-4">No messages</p>
          ) : (
            <div className="bg-white rounded border border-gray-100 p-1">
              <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
            </div>
          )}
        </div>

        {/* Manual QA */}
        <ConversationQASection conversationId={conversation.id} />
      </CardContent>
    </Card>
  )
}
